/* The rules behind the inline reconciliation, kept apart from its rendering.
 *
 * Step 11 removes the standalone wizard. What replaces it is one block inside
 * the one finding that needs it, so the question a reviewer is asked is
 * attached to the thing the answer unlocks rather than standing in front of
 * every finding as a gate.
 *
 * Everything here is a decision about data — which state the check is in, what
 * the dropdowns offer, what a confirmed selection looks like — so it is a pure
 * function and tools/reconcile-flow-check.tsx asserts it without a browser.
 */

import {
  RECONCILE_CHECK_ID,
  bestDetailFor,
  proposeDetailTables,
  proposeRateTables,
  proposeSummaryTables,
  summaryEcmRows,
} from '../../engine/deterministic';
import type {
  Candidate,
  ReconcileSelection,
} from '../../engine/deterministic';
import type { CheckSkip, ExtractedTable, Flag } from '../../types';

export { RECONCILE_CHECK_ID };

/**
 * Rows for checks that have not produced findings are prefixed, so that one can
 * never be mistaken for a finding id — every finding id begins "flag-".
 */
export const PENDING_ID_PREFIX = 'check:';

/** Whether a row id in the findings list belongs to a check rather than a finding. */
export function isPendingRowId(id: string): boolean {
  return id.startsWith(PENDING_ID_PREFIX);
}

/**
 * The id the rail row and the centre pane share for the reconciliation check
 * while it has not produced findings of its own.
 */
export const RECONCILE_ROW_ID = `${PENDING_ID_PREFIX}${RECONCILE_CHECK_ID}`;

/** The id under which a reviewer's "skip" of the rate question is recorded. */
export const RATE_SKIP_ID = `${RECONCILE_CHECK_ID}:rate-table`;

/* ==========================================================================
   Where the check stands
   ========================================================================== */

export type ReconcileState =
  /** Two answers away from running. */
  | 'needs-you'
  /** Nothing in the document has the shape of a savings summary table. */
  | 'unavailable'
  /** The reviewer pressed "Skip for now". Reversible. */
  | 'set-aside'
  /** It ran, against confirmed tables, and every comparison agreed. */
  | 'cleared';

/** Which section of the rail a pending check is drawn in. */
export type PendingGroup = 'needs-you' | 'checked-clean' | 'couldnt-check';

export const PENDING_GROUP_LABELS: Record<PendingGroup, string> = {
  'needs-you': 'Needs your confirmation',
  'checked-clean': 'Checked, nothing found',
  'couldnt-check': 'Couldn’t check',
};

/** Drawn in this order, beneath the plan areas. */
export const PENDING_GROUP_ORDER: PendingGroup[] = [
  'needs-you',
  'checked-clean',
  'couldnt-check',
];

const GROUP_OF: Record<ReconcileState, PendingGroup> = {
  'needs-you': 'needs-you',
  cleared: 'checked-clean',
  'set-aside': 'couldnt-check',
  unavailable: 'couldnt-check',
};

/**
 * One row in the rail for a check that has not produced findings, and the card
 * that opens when it is selected.
 */
export interface PendingCheckRow {
  id: string;
  checkId: string;
  state: ReconcileState;
  group: PendingGroup;
  /** The rail row and the card heading, in a reviewer's language. */
  claim: string;
  /** One line under it. */
  contextLine: string;
  /** Why it has not run, where that is not obvious from the claim. */
  reason: string | null;
  /**
   * What the check needed and where it went looking, shown behind "What it
   * looked for". Absent where the row is an ask rather than a refusal.
   */
  lookedFor?: string;
  /** The short word the rail row carries on its right. */
  mark: string;
}

const CLAIM = 'Whether two tables report different savings for the same measure';

const COPY: Record<ReconcileState, Pick<PendingCheckRow, 'contextLine' | 'reason' | 'mark'>> = {
  'needs-you': {
    contextLine:
      'Two answers from you and this check runs on the plan’s own figures.',
    reason: null,
    mark: 'Needs you',
  },
  'set-aside': {
    contextLine: 'You set this one aside. It can still be answered.',
    reason:
      'Nothing has been compared between the savings tables, so this is not a check that passed — it is a check that has not run.',
    mark: 'Not checked',
  },
  unavailable: {
    contextLine: 'Nothing in this document has the shape of a savings summary.',
    reason:
      'This check needs a project-level savings table — one row per measure and a total — to reconcile the detail tables against. Nothing read out of this document has that shape, so there is nothing to confirm and nothing to compare. That is a statement about the parse, not a verdict on the plan.',
    mark: 'Not checked',
  },
  cleared: {
    contextLine:
      'This check ran against the tables you confirmed. Every figure it could compare agreed exactly.',
    reason: null,
    mark: 'Checked',
  },
};

export interface ReconcileStanding {
  /** Null on the worked example and anywhere else with no parse to read. */
  tables: ExtractedTable[] | null;
  ranChecks: string[];
  skipped: CheckSkip[];
  findings: Flag[];
  /** ReviewRecord.skippedChecks. */
  setAside: string[];
}

/**
 * The reconciliation check's row in the findings list, or null where there
 * should not be one.
 *
 * There is no row once the check has produced findings: the findings are the
 * check, and a row saying so as well would be a second copy of the same fact.
 * There is no row at all without a parse to confirm tables against — the
 * worked example is transcribed, not ingested, and has nothing to reconcile.
 */
export function reconcileRow(standing: ReconcileStanding): PendingCheckRow | null {
  const { tables, ranChecks, findings, setAside } = standing;
  if (tables === null) return null;
  if (findings.some((f) => f.checkId === RECONCILE_CHECK_ID)) return null;

  const state: ReconcileState = ranChecks.includes(RECONCILE_CHECK_ID)
    ? 'cleared'
    : proposeSummaryTables(tables).length === 0
      ? 'unavailable'
      : setAside.includes(RECONCILE_CHECK_ID)
        ? 'set-aside'
        : 'needs-you';

  return {
    id: RECONCILE_ROW_ID,
    checkId: RECONCILE_CHECK_ID,
    state,
    group: GROUP_OF[state],
    claim: CLAIM,
    ...COPY[state],
  };
}

/** The rail's extra sections, in drawn order. Empty groups are not returned. */
export function pendingGroups(
  rows: PendingCheckRow[],
): { group: PendingGroup; label: string; rows: PendingCheckRow[] }[] {
  return PENDING_GROUP_ORDER.map((group) => ({
    group,
    label: PENDING_GROUP_LABELS[group],
    rows: rows.filter((r) => r.group === group),
  })).filter((g) => g.rows.length > 0);
}

/* ==========================================================================
   The answers
   ========================================================================== */

/** What the reviewer has picked, before it becomes a confirmed selection. */
export interface ReconcilePicks {
  summaryTableId: string | null;
  /** Summary row index -> detail table id, or null for "do not reconcile". */
  detailByRow: Record<number, string | null>;
  /** The one further answer, asked inside the finding that needs it. */
  rateTableId: string | null;
  /** tableId -> "row,col" -> the figure the reviewer typed. */
  corrections: Record<string, Record<string, string>>;
}

/** One measure the summary table lists, and the dropdown that belongs to it. */
export interface Measure {
  row: number;
  label: string;
}

export function measuresOf(
  tables: ExtractedTable[],
  summaryTableId: string | null,
): Measure[] {
  const table = tables.find((t) => t.id === summaryTableId);
  if (!table) return [];
  return summaryEcmRows(table).map((row) => ({ row: row.index, label: row.label }));
}

/**
 * Where the block opens: the engine's best guess at each answer, preselected.
 *
 * A guess is a starting point and never a result — nothing is computed from
 * these until the reviewer presses Confirm, and every one of them can be
 * changed first.
 */
export function defaultPicks(tables: ExtractedTable[]): ReconcilePicks {
  const summaryTableId = proposeSummaryTables(tables)[0]?.tableId ?? null;
  return {
    summaryTableId,
    detailByRow: detailGuesses(tables, summaryTableId),
    rateTableId: proposeRateTables(tables)[0]?.tableId ?? null,
    corrections: {},
  };
}

function detailGuesses(
  tables: ExtractedTable[],
  summaryTableId: string | null,
): Record<number, string | null> {
  const details = proposeDetailTables(tables);
  const guesses: Record<number, string | null> = {};
  for (const measure of measuresOf(tables, summaryTableId)) {
    guesses[measure.row] = bestDetailFor(measure.label, details);
  }
  return guesses;
}

/**
 * A different answer to question 1 changes what question 2 is asking about, so
 * the measures — and their guesses — are re-derived. Corrections survive: they
 * are keyed by table, and a table the reviewer corrected is still that table.
 */
export function pickSummary(
  picks: ReconcilePicks,
  tables: ExtractedTable[],
  summaryTableId: string | null,
): ReconcilePicks {
  if (summaryTableId === picks.summaryTableId) return picks;
  return {
    ...picks,
    summaryTableId,
    detailByRow: detailGuesses(tables, summaryTableId),
  };
}

export function pickDetail(
  picks: ReconcilePicks,
  row: number,
  detailTableId: string | null,
): ReconcilePicks {
  return {
    ...picks,
    detailByRow: { ...picks.detailByRow, [row]: detailTableId },
  };
}

/** Records one corrected cell without disturbing any other edit. */
export function applyCorrection(
  picks: ReconcilePicks,
  tableId: string,
  key: string,
  value: string,
): ReconcilePicks {
  return {
    ...picks,
    corrections: {
      ...picks.corrections,
      [tableId]: { ...(picks.corrections[tableId] ?? {}), [key]: value },
    },
  };
}

/**
 * The confirmed selection the engine runs on, or null where question 1 has no
 * answer — without a summary table there is nothing to reconcile against, and
 * the check must not be handed a guess.
 */
export function selectionFrom(
  picks: ReconcilePicks,
  tables: ExtractedTable[],
  options: { includeRate?: boolean } = {},
): ReconcileSelection | null {
  if (!picks.summaryTableId) return null;
  return {
    summaryTableId: picks.summaryTableId,
    ecmMatches: measuresOf(tables, picks.summaryTableId).map((measure) => ({
      summaryRow: measure.row,
      ecmLabel: measure.label,
      detailTableId: picks.detailByRow[measure.row] ?? null,
    })),
    rateTableId: options.includeRate ? (picks.rateTableId ?? undefined) : undefined,
    corrections: picks.corrections,
  };
}

/** The picks a stored selection came from, so a re-run keeps every answer. */
export function picksFrom(
  selection: ReconcileSelection | undefined,
  tables: ExtractedTable[],
): ReconcilePicks {
  const base = defaultPicks(tables);
  if (!selection) return base;
  const detailByRow: Record<number, string | null> = {};
  for (const match of selection.ecmMatches) {
    detailByRow[match.summaryRow] = match.detailTableId;
  }
  return {
    summaryTableId: selection.summaryTableId,
    detailByRow,
    rateTableId: selection.rateTableId ?? base.rateTableId,
    corrections: selection.corrections ?? {},
  };
}

/* ==========================================================================
   The dropdowns
   ========================================================================== */

export interface TableOption {
  value: string;
  label: string;
  /** Why the engine proposed it, where it did. Never the only cue. */
  hint: string | null;
}

/** "Table 1-1 — Proposed Annual Savings Summary · p6". */
export function optionLabel(table: ExtractedTable): string {
  const caption = table.caption.trim();
  return `${table.label}${caption ? ` — ${caption}` : ''} · p${table.page}`;
}

/**
 * Every table read out of the document, with the proposed ones first and the
 * engine's reason attached. The whole document is offered rather than only the
 * candidates: the proposal is a guess, and a reviewer who can see it is wrong
 * must be able to pick the table that is right.
 */
export function tableOptions(
  tables: ExtractedTable[],
  proposed: Candidate[],
): TableOption[] {
  const reasons = new Map(proposed.map((c) => [c.tableId, c.reasons]));
  const rank = new Map(proposed.map((c, i) => [c.tableId, i]));
  return [...tables]
    .sort((a, b) => {
      const ai = rank.get(a.id) ?? Number.MAX_SAFE_INTEGER;
      const bi = rank.get(b.id) ?? Number.MAX_SAFE_INTEGER;
      return ai - bi || a.page - b.page;
    })
    .map((table) => ({
      value: table.id,
      label: optionLabel(table),
      hint: reasons.get(table.id)?.join(' · ') ?? null,
    }));
}

export function summaryOptions(tables: ExtractedTable[]): TableOption[] {
  return tableOptions(tables, proposeSummaryTables(tables));
}

export function detailOptions(tables: ExtractedTable[]): TableOption[] {
  return tableOptions(tables, proposeDetailTables(tables));
}

export function rateOptions(tables: ExtractedTable[]): TableOption[] {
  return tableOptions(tables, proposeRateTables(tables));
}

/** The value the "no table" choice carries, since a select cannot hold null. */
export const NO_TABLE = '';

/**
 * Whether the rate question is worth asking at all. A document with no table
 * carrying a per-unit rate has no answer to give, and asking anyway would be
 * asking for work that unlocks nothing.
 */
export function rateAnswerable(tables: ExtractedTable[]): boolean {
  return proposeRateTables(tables).length > 0;
}

/** The tables the "Correct a figure" disclosure shows: the confirmed ones. */
export function tablesInPlay(
  picks: ReconcilePicks,
  tables: ExtractedTable[],
): ExtractedTable[] {
  const ids = [
    picks.summaryTableId,
    ...measuresOf(tables, picks.summaryTableId).map(
      (m) => picks.detailByRow[m.row] ?? null,
    ),
  ].filter((id): id is string => id !== null);

  const seen = new Set<string>();
  const chosen: ExtractedTable[] = [];
  for (const id of ids) {
    if (seen.has(id)) continue;
    seen.add(id);
    const table = tables.find((t) => t.id === id);
    if (table) chosen.push(table);
  }
  return chosen;
}
